import type { IServerLoadOptions, ServerLoader } from '../dataTableServerSource';

/** Ответ эндпоинта с DataSourceLoadOptionsParser (`DataSourceLoadResult`, IGridResponse и его родня). */
export interface IDxloResponse<T> {
    data?: T[] | null;
    totalCount?: number | null;
    groupCount?: number | null;
}

/** Тело ответа: строки и количество; эндпоинт, которого не просили считать количество, отдаёт просто массив. */
export type DxloListBody<T> = IDxloResponse<T> | T[];

/** Элемент ответа на запрос с `group`: группа уровня без элементов. */
interface IDxloGroup {
    key: unknown;
    caption?: string | null;
    count?: number;
}

/**
 * Запрос к эндпоинту: параметры таблицы → ответ, тело в `data` (ответ axios подходит как есть). Проводной формат
 * (`getLoadOptionsQuery`) у Front и CP разный, поэтому параметры в строку запроса переводит приложение.
 */
export type DxloFetch<T> = (options: IServerLoadOptions, signal: AbortSignal) => Promise<{ data: DxloListBody<T> }>;

/**
 * Источник DataTable поверх эндпоинтов, разбирающих loadOptions DevExtreme (`DataSourceLoadOptionsParser`). Атом
 * шлёт тот же проводной формат, что и CustomStore грида: skip/take/sort/filter/requireTotalCount, а при серверной
 * группировке — group. Без `totalCount` в ответе количество — число пришедших строк.
 */
export function createDxloServerLoad<T>(fetch: DxloFetch<T>): ServerLoader<T> {
    return async (loadOptions: IServerLoadOptions, signal: AbortSignal) => {
        const { data: body } = await fetch(loadOptions, signal);
        const items = (Array.isArray(body) ? body : body?.data) ?? [];
        const totals = Array.isArray(body) ? undefined : body;
        if (loadOptions.group?.length) {
            const groups = items as unknown as IDxloGroup[];
            return {
                rows: [],
                totalCount: totals?.totalCount ?? 0,
                groups: groups.map((g) => ({ key: g.key, caption: g.caption ?? undefined, count: g.count ?? 0 })),
                groupCount: totals?.groupCount ?? groups.length,
            };
        }
        return { rows: items, totalCount: totals?.totalCount ?? items.length };
    };
}
